import React from 'react';
import * as d3 from 'd3';
import { Box, Button, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { CutterHistory } from '../types/cutterHistory';
import CutterTooltip, { CutterTooltipSeverity } from '../common/CutterTooltip';
import DetachablePanel from '../common/DetachablePanel';
import ExportExcel from '../common/ExportExcel';
import { getFormatedStringFromDate, getZipFileLink } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import HelpIcon from '../help/HelpIcon';

// ─── Types ───────────────────────────────────────────────────────────────────

export type TimelineCategory =
    | 'Report upload'
    | 'ETL integration'
    | 'LISA integration'
    | 'Dashboard event'
    | 'Salesforce case'
    | 'Contract'
    | 'Hardware change'
    | 'Software change'
    | 'Maintenance act';

export interface TimelineEvent {
    date: Date;
    category: TimelineCategory;
    description: string;
    severity: CutterTooltipSeverity;
    /** Vertical tab to navigate to on click */
    navTab?: string;
    /** Horizontal subtab to navigate to on click */
    navSubtab?: string;
    /** External URL to open in a new tab on click (e.g. zip download) */
    navLink?: string;
    /** rowNumber to pre-select in the target DataGrid */
    navRowId?: number;
    /** Discriminates software vs softwareVersalis when both share the same subtab */
    navRowSource?: 'software' | 'softwareVersalis';
}

// ─── Severity colours (mirrors CutterTooltip severityStyles) ─────────────────

const SEVERITY_COLOR: Record<CutterTooltipSeverity, string> = {
    success: '#66bb6a',
    info:    '#29b6f6',
    warning: '#ffa726',
    error:   '#ef5350',
};



// Category label ordering (top → bottom)
const CATEGORIES: TimelineCategory[] = [
    'Report upload',
    'ETL integration',
    'LISA integration',
    'Dashboard event',
    'Salesforce case',
    'Contract',
    'Hardware change',
    'Software change',
    'Maintenance act',
];

// ─── Data projection ─────────────────────────────────────────────────────────

function buildEvents(cutter: CutterHistory): TimelineEvent[] {
    const events: TimelineEvent[] = [];

    // Reports
    for (const r of cutter.reports) {
        const hasError = !!r.zipErrorCode;
        const delay = Math.max(0, r.uploadDaysDelay ?? 0);
        const severity: CutterTooltipSeverity = hasError ? 'error' : delay > 7 ? 'warning' : 'success';
        const desc = [
            r.zipFileName,
            delay > 1 ? `Delay: ${delay}d` : null,
            r.zipErrorCode ? `Error: ${r.zipErrorCode}` : null,
            r.zipComment ? r.zipComment : null,
            r.effectiveCuttingTime != null ? `CutTime: ${r.effectiveCuttingTime.toFixed(1)}h` : null,
        ].filter(Boolean).join(' | ');
        events.push({
            date: new Date(r.zipUploadDate), category: 'Report upload', description: desc, severity,
            navTab: 'connexion', navSubtab: 'cutterReports',
            navLink: getZipFileLink(r.zipFileName, r.zipStoredOnDisk),
            navRowId: r.rowNumber,
        });
    }

    // ETL integration — only shown when there is an error
    if (cutter.integration?.fromDate && cutter.integration.log != null) {
        events.push({
            date: new Date(cutter.integration.fromDate),
            category: 'ETL integration',
            description: [
                cutter.integration.zipReport,
                `Step ${cutter.integration.etlStep} : ${cutter.integration.log}`,
            ].filter(Boolean).join(' | '),
            severity: 'error',
            navTab: 'connexion', navSubtab: 'cutterReports',
        });
    }

    // LISA integration rows
    for (const l of cutter.lisa) {
        const delayed = l.lastReportInDays > 30;
        const severity: CutterTooltipSeverity = delayed ? 'warning' : 'success';
        events.push({
            date: new Date(l.lastReport),
            category: 'LISA integration',
            description: `Week ${l.yearWeek} — last report ${l.lastReportInDays} day(s) ago`,
            severity,
            navTab: 'connexion', navSubtab: 'lisaImports',
            navRowId: l.rowNumber,
        });
    }

    // Dashboard events
    for (const d of cutter.dashboard) {
        events.push({
            date: new Date(d.eventEmittedDate),
            category: 'Dashboard event',
            description: `Week ${d.yearWeek} — CMachine ${d.cMachine}, Pilot ${d.pilot}, EvM ${d.eventManager}`,
            severity: 'info',
            navTab: 'connexion', navSubtab: 'dashboard',
            navRowId: d.rowNumber,
        });
    }

    // Salesforce cases
    for (const sf of cutter.salesforceCases) {
        const sev = sf.alertSeverity?.toLowerCase();
        const severity: CutterTooltipSeverity =
            sev === 'high' || sf.priority === 'High' ? 'error' :
            sev === 'medium' || sf.priority === 'Medium' ? 'warning' :
            'info';
        events.push({
            date: new Date(sf.createdDate),
            category: 'Salesforce case',
            description: [
                sf.caseNumber,
                sf.subject,
                sf.status,
                sf.priority ? `Priority: ${sf.priority}` : null,
            ].filter(Boolean).join(' | '),
            severity,
            navTab: 'paperwork', navSubtab: 'salesforce',
        });
    }

    // Contracts
    for (const c of cutter.contract) {
        events.push({
            date: new Date(c.fromDate),
            category: 'Contract',
            description: `${c.contract}${c.isactive ? ' (active)' : ''}${c.todate ? ` → ${getFormatedStringFromDate(c.todate)}` : ''}`,
            severity: c.isactive ? 'success' : 'info',
            navTab: 'paperwork', navSubtab: 'subscriptions',
        });
    }

    // Hardware changes
    for (const h of cutter.hardware) {
        events.push({
            date: new Date(h.fromDate),
            category: 'Hardware change',
            description: [h.machine, h.model, h.market].filter(Boolean).join(' | '),
            severity: 'info',
            navTab: 'system', navSubtab: 'hardware',
            navRowId: h.rowNumber,
        });
    }

    // Software changes
    for (const s of cutter.software) {
        events.push({
            date: new Date(s.fromDate),
            category: 'Software change',
            description: [
                s.pilotVersion ? `Pilot ${s.pilotVersion}` : null,
                s.cmachineVersion ? `CMachine ${s.cmachineVersion}` : null,
                s.bibliobusVersion ? `Biblio ${s.bibliobusVersion}` : null,
                s.eventManagerVersion ? `EvM ${s.eventManagerVersion}` : null,
            ].filter(Boolean).join(' | '),
            severity: 'info',
            navTab: 'system', navSubtab: 'software',
            navRowId: s.rowNumber,
            navRowSource: 'software',
        });
    }
    for (const sv of cutter.softwareVersalis) {
        events.push({
            date: new Date(sv.fromDate),
            category: 'Software change',
            description: [
                sv.lsvVersion ? `LSupervisor ${sv.lsvVersion}` : null,
                sv.lnestVersion ? `LNest ${sv.lnestVersion}` : null,
                sv.lscanVersion ? `LScan ${sv.lscanVersion}` : null,
            ].filter(Boolean).join(' | '),
            severity: 'info',
            navTab: 'system', navSubtab: 'softwareVersalis',
            navRowId: sv.rowNumber,
            navRowSource: 'softwareVersalis',
        });
    }

    // Maintenance interventions (Acts)
    for (const act of (cutter.maintenanceInterventions ?? [])) {
        const desc = [
            act.serviceActName,
            act.serviceActType ? `(${act.serviceActType})` : null,
            act.refCounterName && act.refCounterValue != null
                ? `${act.refCounterName}: ${act.refCounterValue.toFixed(2)}${act.refCounterUnit ? ` ${act.refCounterUnit}` : ''}`
                : null,
            act.userName?.replace(/lectra/gi, '').trim() ?? null,
        ].filter(Boolean).join(' | ');
        events.push({
            date: new Date(act.interventionDate),
            category: 'Maintenance act',
            description: desc,
            severity: 'info',
            navTab: 'maintenance', navSubtab: 'acts',
            navRowId: act.id,
        });
    }

    const validEvents = events.filter(e => !isNaN(e.date.getTime()));
    const latestDate = validEvents.reduce<Date | null>((max, e) => max === null || e.date > max ? e.date : max, null);
    const cutoff = latestDate ? new Date(latestDate.getTime() - 6 * 30 * 24 * 60 * 60 * 1000) : null;
    return validEvents.filter(e => cutoff === null || e.date >= cutoff).sort((a, b) => a.date.getTime() - b.date.getTime());
}

// ─── Chart ───────────────────────────────────────────────────────────────────

const MARGIN = { top: 20, right: 24, bottom: 48, left: 130 };
const MIN_ROW_HEIGHT = 36;

interface ChartProps {
    events: TimelineEvent[];
    width: number;
    height: number;
    xDomainOverride?: [Date, Date] | null;
    onBrushChange?: (range: [Date, Date] | null) => void;
    onBubbleClick?: (ev: TimelineEvent) => void;
}

const TimelineChart: React.FC<ChartProps> = ({ events, width, height: containerHeight, xDomainOverride, onBrushChange, onBubbleClick }) => {
    const theme = useTheme();
    const brushGroupRef = React.useRef<SVGGElement>(null);
    const xScaleRef = React.useRef<d3.ScaleTime<number, number>>(null!);
    const onBrushChangeRef = React.useRef(onBrushChange);
    onBrushChangeRef.current = onBrushChange;

    // Natural (full) domain, needed for scroll zoom-out clamping
    const allDatesRef = React.useRef<Date[]>([]);

    const activeCategories = CATEGORIES.filter(c => events.some(e => e.category === c));

    const innerWidth = Math.max(width - MARGIN.left - MARGIN.right, 100);
    const rowHeight = Math.max(
        MIN_ROW_HEIGHT,
        Math.floor((containerHeight - MARGIN.top - MARGIN.bottom) / Math.max(activeCategories.length, 1))
    );
    const innerHeight = activeCategories.length * rowHeight;
    const height = innerHeight + MARGIN.top + MARGIN.bottom;

    const allDates = events.map(e => e.date);
    allDatesRef.current = allDates;
    const naturalMin = d3.min(allDates) ?? new Date();
    const naturalMax = d3.max(allDates) ?? new Date();
    const span = naturalMax.getTime() - naturalMin.getTime();
    const pad = span * 0.05 || 86400000;
    const xScaleDomain: [Date, Date] = xDomainOverride ?? [
        new Date(naturalMin.getTime() - pad),
        new Date(naturalMax.getTime() + pad),
    ];
    const xScale = d3.scaleTime()
        .domain(xScaleDomain)
        .range([0, innerWidth]);
    xScaleRef.current = xScale;

    // Attach D3 brushX to the brush <g> whenever dimensions change.
    // Kept permanently mounted (never conditionally rendered) so D3 internal
    // state is never lost across zoom/reset cycles.
    React.useEffect(() => {
        if (!brushGroupRef.current) return;
        const brush = d3.brushX<unknown>()
            .extent([[0, 0], [innerWidth, innerHeight]])
            .on('end', (event: d3.D3BrushEvent<unknown>) => {
                if (!event.sourceEvent) return; // skip programmatic clears
                if (!event.selection) return;
                const [x0, x1] = (event.selection as [number, number]).map(
                    px => xScaleRef.current.invert(px)
                ) as [Date, Date];
                if (x0 < x1) {
                    // Clear visual selection before zooming so it doesn't linger
                    d3.select(brushGroupRef.current!).call(brush.move, null);
                    onBrushChangeRef.current?.([x0, x1]);
                }
            });
        const sel = d3.select(brushGroupRef.current);
        sel.call(brush);
        sel.select<SVGRectElement>('.selection')
            .style('fill', '#1976d2')
            .style('fill-opacity', '0.12')
            .style('stroke', '#1976d2')
            .style('stroke-width', '1');
    }, [innerWidth, innerHeight]);

    const yScale = d3.scaleBand()
        .domain(activeCategories)
        .range([0, innerHeight])
        .padding(0.3);

    const xTicks = xScale.ticks(Math.max(3, Math.min(10, Math.floor(innerWidth / 80))));
    const xSpanMs = xScaleDomain[1].getTime() - xScaleDomain[0].getTime();
    const xSpanHours = xSpanMs / (1000 * 60 * 60);
    const xSpanDays = xSpanHours / 24;
    const xTickFormat = xSpanHours < 3
        ? d3.timeFormat('%H:%M')
        : xSpanDays < 2
        ? d3.timeFormat('%d %b %H:%M')
        : xSpanDays < 60
        ? d3.timeFormat('%d %b')
        : d3.timeFormat('%b %Y');

    // Bucket events by (category, time-unit) to compute bubble sizes
    const getBucketKey = (date: Date, cat: TimelineCategory): string => {
        const d = new Date(date);
        if (xSpanHours < 3) {
            d.setSeconds(0, 0);
        } else if (xSpanDays < 2) {
            d.setMinutes(0, 0, 0);
        } else if (xSpanDays < 60) {
            d.setHours(0, 0, 0, 0);
        } else {
            d.setDate(d.getDate() - d.getDay());
            d.setHours(0, 0, 0, 0);
        }
        return `${cat}|${d.getTime()}`;
    };
    const bucketCounts: Record<string, number> = {};
    for (const ev of events) {
        const k = getBucketKey(ev.date, ev.category);
        bucketCounts[k] = (bucketCounts[k] ?? 0) + 1;
    }
    const maxBucketCount = Math.max(1, ...Object.values(bucketCounts));
    const radiusScale = d3.scaleSqrt().domain([1, maxBucketCount]).range([4, 12]);

    const gridColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
    const textColor = theme.palette.text.secondary;
    const axisColor = theme.palette.divider;

    const handleWheel = React.useCallback((e: React.WheelEvent<SVGSVGElement>) => {
        e.preventDefault();
        const svgRect = e.currentTarget.getBoundingClientRect();
        // Mouse X relative to the chart area (after left margin)
        const mouseX = e.clientX - svgRect.left - MARGIN.left;
        const clampedMouseX = Math.max(0, Math.min(innerWidth, mouseX));
        const mouseDate = xScaleRef.current.invert(clampedMouseX).getTime();

        const [t0, t1] = xScaleRef.current.domain().map(d => d.getTime()) as [number, number];
        const spanMs = t1 - t0;

        // zoom factor: scroll up = zoom in (0.8x), scroll down = zoom out (1.25x)
        const factor = e.deltaY < 0 ? 0.8 : 1.25;
        const newSpan = spanMs * factor;

        // Clamp zoom-out to natural extent
        const dates = allDatesRef.current;
        const natMin = (d3.min(dates) ?? new Date()).getTime();
        const natMax = (d3.max(dates) ?? new Date()).getTime();
        const natSpan = natMax - natMin;
        const pad = natSpan * 0.05 || 86400000;
        const hardMin = natMin - pad;
        const hardMax = natMax + pad;
        const maxSpan = hardMax - hardMin;

        const ratio = (mouseDate - t0) / spanMs;
        let newT0 = mouseDate - ratio * newSpan;
        let newT1 = newT0 + newSpan;

        // Clamp to natural bounds when zooming out
        if (newSpan >= maxSpan) {
            onBrushChangeRef.current?.(null);
            return;
        }
        if (newT0 < hardMin) { newT0 = hardMin; newT1 = newT0 + newSpan; }
        if (newT1 > hardMax) { newT1 = hardMax; newT0 = newT1 - newSpan; }

        onBrushChangeRef.current?.([new Date(newT0), new Date(newT1)]);
    }, [innerWidth]);

    return (
        <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }} onWheel={handleWheel}>
            <defs>
                <clipPath id="cutter-timeline-clip">
                    <rect x={0} y={0} width={innerWidth} height={innerHeight} />
                </clipPath>
            </defs>
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>

                {/* Horizontal grid lines */}
                {activeCategories.map(cat => (
                    <line
                        key={cat}
                        x1={0} x2={innerWidth}
                        y1={(yScale(cat) ?? 0) + yScale.bandwidth() / 2}
                        y2={(yScale(cat) ?? 0) + yScale.bandwidth() / 2}
                        stroke={gridColor} strokeWidth={1}
                    />
                ))}

                {/* Vertical grid lines */}
                {xTicks.map((t, i) => (
                    <line
                        key={i}
                        x1={xScale(t)} x2={xScale(t)}
                        y1={0} y2={innerHeight}
                        stroke={gridColor} strokeWidth={1}
                    />
                ))}

                {/* X axis */}
                <line x1={0} x2={innerWidth} y1={innerHeight} y2={innerHeight} stroke={axisColor} />
                {xTicks.map((t, i) => (
                    <g key={i} transform={`translate(${xScale(t)},${innerHeight})`}>
                        <line y2={4} stroke={axisColor} />
                        <text
                            y={14} textAnchor="middle"
                            fontSize="0.68rem" fill={textColor}
                        >
                            {xTickFormat(t)}
                        </text>
                    </g>
                ))}

                {/* Y axis labels */}
                {activeCategories.map(cat => (
                    <text
                        key={cat}
                        x={-8}
                        y={(yScale(cat) ?? 0) + yScale.bandwidth() / 2}
                        textAnchor="end"
                        dominantBaseline="central"
                        fontSize="0.72rem"
                        fill={textColor}
                    >
                        {cat}
                    </text>
                ))}

                {/* Brush overlay — before bubbles so circles stay on top and CutterTooltip works */}
                <g ref={brushGroupRef} />

                {/* Bubbles */}
                <g clipPath="url(#cutter-timeline-clip)">
                {events.map((ev, i) => {
                    const cx = xScale(ev.date);
                    const cy = (yScale(ev.category) ?? 0) + yScale.bandwidth() / 2;
                    const r = radiusScale(bucketCounts[getBucketKey(ev.date, ev.category)] ?? 1);
                    const color = SEVERITY_COLOR[ev.severity];
                    return (
                        <CutterTooltip
                            key={i}
                            title={
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                        {getFormatedStringFromDate(ev.date)} — {ev.category}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.3, opacity: 0.9 }}>
                                        {ev.description}
                                    </Typography>
                                </Box>
                            }
                            copyText={`${getFormatedStringFromDate(ev.date)} | ${ev.category} | ${ev.description}`}
                            severity={ev.severity}
                            placement="top"
                        >
                            <circle
                                cx={cx} cy={cy} r={r}
                                fill={color}
                                fillOpacity={0.85}
                                stroke={color}
                                strokeWidth={1.5}
                                style={{ cursor: (ev.navTab != null || ev.navLink != null) ? 'pointer' : 'default' }}
                                onClick={onBubbleClick ? () => onBubbleClick(ev) : undefined}
                            />
                        </CutterTooltip>
                    );
                })}
                </g>
            </g>
        </svg>
    );
};

// ─── Panel wrapper ────────────────────────────────────────────────────────────

interface CutterTimelinePanelProps {
    open: boolean;
    onClose: () => void;
    cutter: CutterHistory;
    brushRange: [Date, Date] | null;
    onBrushRangeChange: (range: [Date, Date] | null) => void;
}

const CutterTimelinePanel: React.FC<CutterTimelinePanelProps> = ({ open, onClose, cutter, brushRange, onBrushRangeChange }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [chartSize, setChartSize] = React.useState({ width: 0, height: 0 });
    const navigate = useNavigate();
    const theme = useTheme();
    const exportFill = theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark;

    // Compute docked-left defaults (only when viewport is wide enough to show gutters)
    const { panelWidth: timelinePanelWidth, panelHeight: timelinePanelHeight, defaultPosition: timelineDefaultPosition } = React.useMemo(() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const gutterW = Math.floor((vw - 936) / 2);
        if (gutterW < 150) return { panelWidth: undefined, panelHeight: undefined, defaultPosition: undefined };
        // 15px margin each side → width = gutterW - 30
        // x = -(vw + 936) / 4  (positions left edge 15px from viewport left)
        // height = top-1/3 to bottom-10% = 17/30 of vh
        const panelHeight = Math.round(vh * 17 / 30);
        const dragY = Math.round(vh / 5 - (vh - panelHeight) / 2);
        return {
            panelWidth: gutterW - 30,
            panelHeight,
            defaultPosition: { x: Math.round(-(vw + 936) / 4), y: dragY },
        };
    }, []);

    const handleBubbleClick = React.useCallback((ev: TimelineEvent) => {
        if (ev.navLink) {
            const a = document.createElement('a');
            a.href = ev.navLink;
            a.download = '';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
        if (ev.navTab && ev.navSubtab) {
            let url = `/bookmarks?serialnumber=${encodeURIComponent(cutter.serialNumber)}` +
                `&tab=${encodeURIComponent(ev.navTab)}` +
                `&subtab=${encodeURIComponent(ev.navSubtab)}`;
            if (ev.navRowId != null) url += `&rowid=${ev.navRowId}`;
            if (ev.navRowSource) url += `&rowsource=${encodeURIComponent(ev.navRowSource)}`;
            navigate(url);
        }
    }, [cutter.serialNumber, navigate]);

    React.useEffect(() => {
        if (!open) return;
        let ro: ResizeObserver;
        // Defer to after the Dialog has finished its layout pass
        const raf = requestAnimationFrame(() => {
            const el = containerRef.current;
            if (!el) return;
            ro = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const w = entry.contentRect.width;
                    const h = entry.contentRect.height;
                    if (w > 0 && h > 0) setChartSize({ width: w, height: h });
                }
            });
            ro.observe(el); // fires immediately with current size
        });
        return () => {
            cancelAnimationFrame(raf);
            ro?.disconnect();
        };
    }, [open]);

    const events = React.useMemo(() => buildEvents(cutter), [cutter]);

    const exportColumns = [
        { key: 'date', header: 'Date', width: 22 },
        { key: 'category', header: 'Category', width: 20 },
        { key: 'description', header: 'Description', width: 80 },
        { key: 'severity', header: 'Severity', width: 12 },
    ];
    const exportRows = events.map(ev => ({
        date: getFormatedStringFromDate(ev.date),
        category: ev.category,
        description: ev.description,
        severity: ev.severity,
    }));

    return (
        <DetachablePanel
            open={open}
            onClose={onClose}
            panelId="timeline"
            title={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>Timeline</span>
                    <HelpIcon helpContext="cutter-timeline" placement="bottom" size="small" />
                </Stack>
            }
            subtitle={<Typography variant='subtitle2'>{cutter.serialNumber}</Typography>}
            width={timelinePanelWidth ?? { xs: '95vw', sm: '90vw', md: '85vw', lg: '80vw' }}
            height={timelinePanelHeight}
            defaultPosition={timelineDefaultPosition}
            headerActions={exportRows.length > 0 && (
                <Tooltip title="Export timeline to Excel" placement="left">
                    <span>
                        <ExportExcel
                            label={`${cutter.serialNumber} timeline`}
                            results={exportRows}
                            total={exportRows.length}
                            exportType="details"
                            columns={exportColumns}
                            size="small"
                            sx={{ width: '28px', height: '28px', fill: exportFill }}
                        />
                    </span>
                </Tooltip>
            )}
        >
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
                {brushRange && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pt: 0.5, flexShrink: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                            Zoomed: {getFormatedStringFromDate(brushRange[0])} → {getFormatedStringFromDate(brushRange[1])}
                        </Typography>
                        <Tooltip title="Reset zoom to full range">
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onBrushRangeChange(null)}
                                sx={{ flexShrink: 0, py: 0.25, lineHeight: 1, whiteSpace: 'nowrap' }}
                            >
                                Reset zoom
                            </Button>
                        </Tooltip>
                    </Box>
                )}
                <Box ref={containerRef} sx={{ flex: 1, overflow: 'auto', p: 1, boxSizing: 'border-box', minHeight: 0 }}>
                    {events.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No timeline events available.
                        </Typography>
                    ) : chartSize.width > 0 && chartSize.height > 0 ? (
                        <TimelineChart events={events} width={chartSize.width} height={chartSize.height}
                            xDomainOverride={brushRange} onBrushChange={onBrushRangeChange}
                            onBubbleClick={handleBubbleClick} />
                    ) : null}
                </Box>
            </Box>
        </DetachablePanel>
    );
};

export default CutterTimelinePanel;
